/**
 * GIWAHS Adaptive Journey Core Engine — JourneyEngine (AJIP-16.02.003 · WS1)
 * ---------------------------------------------------------------------
 * Single orchestrator for every Adaptive Journey in the GIWAHS
 * platform. No competing engine may exist. All journey behaviour flows
 * through this class.
 *
 * Responsibilities (per directive WS1)
 *   Registration    register(definition)
 *   Discovery       discover(), has(id), get(id)
 *   Loading         load(id)      → JourneyContext
 *   Activation      activate(id)  → JourneyContext at STARTED
 *   Execution       execute(id, event)
 *   Suspension      suspend(id)   → WAITING
 *   Completion      complete(id)  → COMPLETED
 *   Cancellation    cancel(id)    → CANCELLED
 *   Restoration     restore(id, snapshot) — in-session recovery only.
 *                   Cross-session persistence is out of scope
 *                   (deferred to AJIP-16.02.004 per directive).
 *
 * Feature-Flag Isolation
 *   The engine reads isFeatureEnabled('JOURNEYS_ENABLED') at
 *   construction and every mutating call. When disabled every public
 *   method throws FlagDisabledError. Read-only queries (has, discover,
 *   size, snapshot) return safe empty values when disabled.
 */
import { isFeatureEnabled } from '../../lib/featureFlags';
import { JourneyRegistry } from './JourneyRegistry';
import { JourneyContext } from './JourneyContext';
import { STATES, isTerminalState } from './JourneyState';
import { evaluateTransition, raiseInvalidTransition } from './TransitionEngine';
import { Observability, OBSERVABILITY_EVENTS } from './Observability';
import { LIFECYCLE_EVENTS } from './JourneyLifecycle';
import {
  FlagDisabledError,
  NotRegisteredError,
  InvalidRequestError,
  InvalidTransitionError,
} from './JourneyErrors';

const _requireEnabled = (engine) => {
  if (!engine._enabled) {
    throw new FlagDisabledError('JOURNEYS_ENABLED is false. Engine is inert.');
  }
};

class JourneyEngine {
  constructor({ registry, observability } = {}) {
    this._enabled = isFeatureEnabled('JOURNEYS_ENABLED');
    this._registry = registry instanceof JourneyRegistry ? registry : new JourneyRegistry();
    this._observability = observability instanceof Observability ? observability : new Observability();
    this._contexts = new Map();
    this._flagStatus = Object.freeze({ JOURNEYS_ENABLED: this._enabled });
  }

  get enabled() { return this._enabled; }
  get registry() { return this._registry; }
  get observability() { return this._observability; }
  get flagStatus() { return this._flagStatus; }

  register(definition) {
    _requireEnabled(this);
    return this._registry.register(definition);
  }

  discover() {
    if (!this._enabled) return Object.freeze([]);
    return this._registry.list();
  }

  has(identifier) {
    if (!this._enabled) return false;
    return this._registry.has(identifier);
  }

  get(identifier) {
    _requireEnabled(this);
    return this._registry.get(identifier);
  }

  size() {
    if (!this._enabled) return 0;
    return this._registry.size();
  }

  load(identifier) {
    _requireEnabled(this);
    if (this._contexts.has(identifier)) return this._contexts.get(identifier);
    const definition = this._registry.get(identifier);
    const ctx = new JourneyContext({ definition, flagStatus: this._flagStatus });
    this._contexts.set(identifier, ctx);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_STARTED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.INIT,
      state: ctx.currentState,
    });
    return ctx;
  }

  activate(identifier) {
    _requireEnabled(this);
    const ctx = this.load(identifier);
    if (ctx.currentState !== STATES.NOT_STARTED) {
      throw new InvalidRequestError(
        `Cannot activate journey '${identifier}' from state ${ctx.currentState}`,
      );
    }
    ctx._setState(STATES.STARTED);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_STARTED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.ENTRY,
      state: ctx.currentState,
    });
    return ctx;
  }

  execute(identifier, event) {
    _requireEnabled(this);
    const ctx = this._contexts.get(identifier);
    if (!ctx) throw new NotRegisteredError(`Journey '${identifier}' is not active. Call activate() first.`);
    const decision = evaluateTransition({
      from: ctx.currentState,
      event,
      journey: ctx.activeJourney,
    });
    if (!decision.ok) raiseInvalidTransition(decision);
    ctx._setState(decision.to);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_STARTED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.PROGRESSION,
      state: ctx.currentState,
      event,
    });
    if (isTerminalState(decision.to)) {
      const kind = {
        [STATES.COMPLETED]: OBSERVABILITY_EVENTS.JOURNEY_COMPLETED,
        [STATES.CANCELLED]: OBSERVABILITY_EVENTS.JOURNEY_CANCELLED,
        [STATES.FAILED]: OBSERVABILITY_EVENTS.JOURNEY_FAILED,
      }[decision.to];
      this._observability.emit(kind, {
        identifier,
        lifecycle: LIFECYCLE_EVENTS.COMPLETION,
        state: ctx.currentState,
      });
    }
    return decision;
  }

  suspend(identifier) {
    _requireEnabled(this);
    const ctx = this._contexts.get(identifier);
    if (!ctx) throw new NotRegisteredError(`Journey '${identifier}' is not active.`);
    if (isTerminalState(ctx.currentState)) {
      throw new InvalidTransitionError(`Cannot suspend a terminal journey '${identifier}'`);
    }
    ctx._setState(STATES.WAITING);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_PAUSED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.PAUSE,
      state: ctx.currentState,
    });
    return ctx;
  }

  resume(identifier) {
    _requireEnabled(this);
    const ctx = this._contexts.get(identifier);
    if (!ctx) throw new NotRegisteredError(`Journey '${identifier}' is not active.`);
    if (ctx.currentState !== STATES.WAITING) {
      throw new InvalidTransitionError(
        `Cannot resume journey '${identifier}' from state ${ctx.currentState}`,
      );
    }
    ctx._setState(STATES.IN_PROGRESS);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_RESUMED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.RESUME,
      state: ctx.currentState,
    });
    return ctx;
  }

  complete(identifier) {
    _requireEnabled(this);
    const ctx = this._contexts.get(identifier);
    if (!ctx) throw new NotRegisteredError(`Journey '${identifier}' is not active.`);
    if (isTerminalState(ctx.currentState)) {
      throw new InvalidTransitionError(`Cannot complete a terminal journey '${identifier}'`);
    }
    ctx._setState(STATES.COMPLETED);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_COMPLETED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.COMPLETION,
      state: ctx.currentState,
    });
    return ctx;
  }

  cancel(identifier) {
    _requireEnabled(this);
    const ctx = this._contexts.get(identifier);
    if (!ctx) throw new NotRegisteredError(`Journey '${identifier}' is not active.`);
    if (isTerminalState(ctx.currentState)) {
      throw new InvalidTransitionError(`Cannot cancel a terminal journey '${identifier}'`);
    }
    ctx._setState(STATES.CANCELLED);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_CANCELLED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.EXIT,
      state: ctx.currentState,
    });
    return ctx;
  }

  /**
   * In-session recovery only. Cross-session persistence is explicitly
   * out of scope for AJIP-16.02.003 and deferred to AJIP-16.02.004.
   *
   * Accepts a prior snapshot produced by JourneyContext.snapshot() and
   * restores state/stage/progress/metadata onto a freshly loaded
   * context. The definition is re-resolved from the registry; the
   * snapshot is trusted only for mutable dimensions.
   */
  restore(identifier, snapshot) {
    _requireEnabled(this);
    if (!snapshot || typeof snapshot !== 'object') {
      throw new InvalidRequestError('restore() requires a prior snapshot object');
    }
    if (snapshot.identifier !== identifier) {
      throw new InvalidRequestError('Snapshot identifier does not match');
    }
    // If a context already exists we refuse — restoration is a rehydration,
    // not a live overwrite. Consumers must dispose() before restore.
    if (this._contexts.has(identifier)) {
      throw new InvalidRequestError(`Journey '${identifier}' is already active. Dispose first.`);
    }
    const ctx = this.load(identifier); // NOT_STARTED
    if (snapshot.currentState) ctx._setState(snapshot.currentState);
    if (snapshot.currentStage !== undefined) ctx._setStage(snapshot.currentStage);
    if (typeof snapshot.progress === 'number') ctx._setProgress(snapshot.progress);
    if (snapshot.metadata) ctx._mergeMetadata(snapshot.metadata);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_RESUMED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.RECOVERY,
      state: ctx.currentState,
    });
    return ctx;
  }

  /**
   * Published stage-transition interface (AJIP-16.02.007 · Founder Ruling B8).
   *
   * Sole authorised path for external orchestrators (e.g.
   * BuyerJourneyOrchestrator) to update a journey context's currentStage.
   * Direct external use of JourneyContext._setStage() is prohibited.
   *
   * The engine remains framework-agnostic. This method contains no
   * Buyer-specific logic; it validates the instance, the stage value,
   * emits a stage-change observability event, and returns the updated
   * JourneyContext.
   */
  updateStage(identifier, nextStage) {
    _requireEnabled(this);
    if (typeof identifier !== 'string' || identifier.length === 0) {
      throw new InvalidRequestError('updateStage: identifier must be non-empty string');
    }
    if (typeof nextStage !== 'string' || nextStage.length === 0) {
      throw new InvalidRequestError('updateStage: nextStage must be non-empty string');
    }
    const ctx = this._contexts.get(identifier);
    if (!ctx) {
      throw new NotRegisteredError(`Journey '${identifier}' is not active.`);
    }
    ctx._setStage(nextStage);
    this._observability.emit(OBSERVABILITY_EVENTS.JOURNEY_STARTED, {
      identifier,
      lifecycle: LIFECYCLE_EVENTS.PROGRESSION,
      state: ctx.currentState,
      stage: nextStage,
    });
    return ctx;
  }

  dispose(identifier) {
    _requireEnabled(this);
    return this._contexts.delete(identifier);
  }

  contextOf(identifier) {
    if (!this._enabled) return null;
    return this._contexts.get(identifier) || null;
  }
}

export { JourneyEngine };
